import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Button,
  CircularProgress
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HistoryIcon from '@mui/icons-material/History';
import { handleError } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const ClinicalHistoriesReport = () => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const snackbar = useSnackbar();

  const handleExportJSON = async () => {
    setIsExporting(true);
    try {
      const params = {
        type: 'OWNERS_PETS_CLINICS',
        page: 1
      };
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}reports`, 
        {
          params: params, 
          withCredentials: true
        }
      );

      if (response.data && response.data.status === "SUCCESS" && response.data.data) {
        let jsonData;
        if (typeof response.data.data === 'string') {
          try {
            jsonData = JSON.parse(response.data.data);
          } catch {
            jsonData = response.data.data;
          }
        } else {
          jsonData = response.data.data;
        }
        
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { 
          type: 'application/json' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
        link.setAttribute('download', `historias_clinicas_${dateStr}_${timeStr}.json`);
        
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        snackbar(t('reports.clinicalHistoriesExported'));
      } else {
        throw new Error(t('reports.exportInvalidFormat'));
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {t('reports.clinicalHistoriesTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('reports.clinicalHistoriesDesc')}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <HistoryIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('reports.clinicalHistoriesComplete')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('reports.clinicalHistoriesFileDesc')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="large"
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
          onClick={handleExportJSON}
          disabled={isExporting}
          sx={{ minWidth: 200 }}
        >
          {isExporting ? t('reports.generatingFile') : t('reports.downloadJson')}
        </Button>
      </Paper>
    </Box>
  );
};

export default ClinicalHistoriesReport;
